import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { Invoice, InvoiceLineItem, InvoiceLineItemValidation } from 'src/redux/billing/billing.types';
import { usePatchInvoiceLineItemValidationMutation } from 'src/redux/billing/billing.endpoints';
import { fieldLabel } from './line-item-validation-details-content';

interface MissingLineItemValidationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  validations: InvoiceLineItemValidation[];
  invoices: Invoice[];
  orphanInvoiceLineItems?: InvoiceLineItem[];
  invoiceGroupId: string | undefined;
  refetchValidations: () => void | Promise<unknown>;
  refetchInvoiceGroup: () => void | Promise<void>;
}

function buildLineItemLookup(
  invoices: Invoice[],
  orphanLineItems: InvoiceLineItem[] = []
): Map<string, { lineItem: InvoiceLineItem; invoiceLabel: string }> {
  const map = new Map<string, { lineItem: InvoiceLineItem; invoiceLabel: string }>();
  for (const inv of invoices) {
    const invoiceLabel =
      inv.ext_invoice_number != null && inv.ext_invoice_number !== ''
        ? String(inv.ext_invoice_number)
        : inv.id;
    for (const li of inv.invoice_line_items ?? []) {
      map.set(li.id, { lineItem: li, invoiceLabel });
    }
  }
  for (const li of orphanLineItems) {
    if (!map.has(li.id)) {
      map.set(li.id, { lineItem: li, invoiceLabel: 'Unassigned' });
    }
  }
  return map;
}

function formatDetails(details: Record<string, unknown> | null): string {
  if (details == null || Object.keys(details).length === 0) {
    return '—';
  }
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return String(details);
  }
}

const MissingLineItemValidationsModal: React.FC<MissingLineItemValidationsModalProps> = ({
  open,
  onOpenChange,
  validations,
  invoices,
  orphanInvoiceLineItems = [],
  invoiceGroupId,
  refetchValidations,
  refetchInvoiceGroup,
}) => {
  const [patchValidation] = usePatchInvoiceLineItemValidationMutation();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const lookup = useMemo(
    () => buildLineItemLookup(invoices, orphanInvoiceLineItems),
    [invoices, orphanInvoiceLineItems]
  );

  const rows = useMemo(
    () =>
      [...validations].sort((a, b) => {
        const la = lookup.get(a.line_item)?.lineItem.name ?? '';
        const lb = lookup.get(b.line_item)?.lineItem.name ?? '';
        return la.localeCompare(lb);
      }),
    [validations, lookup]
  );

  const handleIgnore = async(v: InvoiceLineItemValidation) => {
    if (pendingId != null || invoiceGroupId == null || invoiceGroupId === '') {
      return;
    }
    setPendingId(v.id);
    try {
      await patchValidation({
        id: v.id,
        data: { status: 'archived' },
        invoiceGroupId,
      }).unwrap();
      await refetchValidations();
      await refetchInvoiceGroup();
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-5xl overflow-y-auto bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>
            Missing line item validations
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">
          Rows marked missing from validation sources. Review details and resolve in the invoice drawer where needed.
        </p>
        <div className="rounded-md border border-gray-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">
                  Line item
                </TableHead>
                <TableHead className="text-xs">
                  Invoice
                </TableHead>
                <TableHead className="text-xs">
                  Field
                </TableHead>
                <TableHead className="text-xs">
                  Source
                </TableHead>
                <TableHead className="text-xs">
                  Value
                </TableHead>
                <TableHead className="text-xs">
                  Note
                </TableHead>
                <TableHead className="text-xs">
                  Details
                </TableHead>
                <TableHead className="w-[200px] text-right text-xs">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-6 text-center text-sm text-gray-600">
                    No missing validations to show.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((v) => {
                  const found = lookup.get(v.line_item);
                  const lineName = found?.lineItem.name ?? v.line_item;
                  const rowBusy = pendingId === v.id;
                  return (
                    <TableRow key={v.id}>
                      <TableCell className="max-w-[180px] align-top text-xs">
                        <span className="font-medium text-gray-900">
                          {lineName}
                        </span>
                      </TableCell>
                      <TableCell className="align-top text-xs text-gray-700">
                        {found?.invoiceLabel ?? '—'}
                      </TableCell>
                      <TableCell className="align-top text-xs">
                        {fieldLabel(v.field)}
                      </TableCell>
                      <TableCell className="align-top text-xs">
                        {v.source.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell className="max-w-[120px] align-top font-mono text-xs break-all">
                        {v.value != null && v.value !== '' ? v.value : '—'}
                      </TableCell>
                      <TableCell className="max-w-[140px] align-top text-xs break-words">
                        {v.note ?? '—'}
                      </TableCell>
                      <TableCell className="max-w-[220px] align-top">
                        <pre className="whitespace-pre-wrap break-all text-[10px] text-gray-800">
                          {formatDetails(v.details)}
                        </pre>
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex w-full flex-wrap items-center justify-end gap-1.5">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            disabled={rowBusy || pendingId != null || invoiceGroupId == null}
                            onClick={() => handleIgnore(v)}
                          >
                            {rowBusy ? 'Ignoring…' : 'Ignore'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="black"
                            className="h-7 text-xs text-white hover:bg-gray-900"
                            disabled
                            title="Create line item from this validation — coming soon"
                          >
                            Create
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MissingLineItemValidationsModal;
